import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { TEAM_ROLES } from '../../../data/nurseAppModels';
import UserSelector from '../../Admin/UserSelector';
import GiftButton from '../Gift/GiftButton';
import { GIFT_CONTEXTS } from '../../../utils/nurseGiftManager';

const TeamDetailPage = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { teams, handleRequest, removeMember, inviteMember, changeMemberRole, currentTeam, setTeam: setCurrentTeam } = useTeam();
  const { currentUser } = useAuth();
  const [team, setTeam] = useState(null);
  const [requests, setRequests] = useState([]);
  const [pendingInvitation, setPendingInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('members');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inviting, setInviting] = useState(false);

  // Use teamId from URL as primary source (for navigation from notifications)
  // Only fallback to currentTeam.id if teamId is not available
  const effectiveTeamId = teamId || currentTeam?.id;

  // Sync currentTeam with teamId from URL when navigating from notifications
  useEffect(() => {
    if (teamId && teams.length > 0) {
      const teamFromUrl = teams.find(t => t.id === teamId);
      if (teamFromUrl && (!currentTeam || currentTeam.id !== teamId)) {
        // Update currentTeam to match URL teamId (so top menu shows correct team)
        setCurrentTeam(teamFromUrl);
      }
    }
  }, [teamId, teams, currentTeam, setCurrentTeam]);

  useEffect(() => {
    if (!effectiveTeamId) return;
    
    fetchTeamData();
    fetchTeamRequests();
    fetchPendingInvitation();
  }, [effectiveTeamId, currentUser]);

  const fetchTeamData = async () => {
    // Use teamId from URL first, then fallback to currentTeam.id
    const targetTeamId = teamId || currentTeam?.id;
    if (!targetTeamId) return;
    
    try {
      const teamRef = doc(db, 'teams', targetTeamId);
      const teamSnap = await getDoc(teamRef);
      
      if (teamSnap.exists()) {
        const teamData = teamSnap.data();
        const userTeam = teams.find(t => t.id === targetTeamId);
        
        // Remove duplicate members (keep first occurrence of each uid)
        const uniqueMembers = [];
        const seenUids = new Set();
        if (teamData.members && Array.isArray(teamData.members)) {
          teamData.members.forEach(member => {
            if (member.uid && !seenUids.has(member.uid)) {
              seenUids.add(member.uid);
              uniqueMembers.push(member);
            }
          });
        }
        
        setTeam({
          id: teamSnap.id,
          ...teamData,
          members: uniqueMembers,
          userRole: userTeam?.userRole
        });
      }
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamRequests = async () => {
    // Use teamId from URL first, then fallback to currentTeam.id
    const targetTeamId = teamId || currentTeam?.id;
    if (!targetTeamId) return;
    
    try {
      const requestsRef = collection(db, 'teamRequests');
      const q = query(
        requestsRef,
        where('teamId', '==', targetTeamId),
        where('status', '==', 'PENDING')
      );
      const snapshot = await getDocs(q);
      setRequests(snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const fetchPendingInvitation = async () => {
    if (!currentUser) return;
    
    // Use teamId from URL first, then fallback to currentTeam.id
    const targetTeamId = teamId || currentTeam?.id;
    if (!targetTeamId) return;
    
    try {
      const requestsRef = collection(db, 'teamRequests');
      const q = query(
        requestsRef,
        where('teamId', '==', targetTeamId),
        where('userId', '==', currentUser.uid),
        where('status', '==', 'PENDING'),
        where('type', '==', 'INVITATION')
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const invitation = {
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data()
        };
        setPendingInvitation(invitation);
      } else {
        setPendingInvitation(null);
      }
    } catch (error) {
      console.error('Error fetching pending invitation:', error);
      setPendingInvitation(null);
    }
  };

  const handleApproveRequest = async (requestId, teamRole = TEAM_ROLES.MEMBER) => {
    const result = await handleRequest(requestId, 'APPROVE', teamRole);
    if (result.success) {
      fetchTeamData();
      fetchTeamRequests();
      fetchPendingInvitation();
    } else {
      alert('เกิดข้อผิดพลาด: ' + result.error);
    }
  };

  const handleRejectRequest = async (requestId) => {
    const result = await handleRequest(requestId, 'REJECT');
    if (result.success) {
      fetchTeamRequests();
      fetchPendingInvitation();
    } else {
      alert('เกิดข้อผิดพลาด: ' + result.error);
    }
  };
  
  const handleAcceptInvitation = async () => {
    if (!pendingInvitation) return;
    const teamRole = pendingInvitation.teamRole || TEAM_ROLES.MEMBER;
    await handleApproveRequest(pendingInvitation.id, teamRole);
  };
  
  const handleRejectInvitation = async () => {
    if (!pendingInvitation) return;
    await handleRejectRequest(pendingInvitation.id);
  };

  const handleRemoveMember = async (userId) => {
    // Use teamId from URL first, then fallback to currentTeam.id
    const targetTeamId = teamId || currentTeam?.id;
    if (!targetTeamId) return;
    
    if (window.confirm('คุณแน่ใจหรือไม่ที่จะลบสมาชิกคนนี้ออกจากทีม?')) {
      const result = await removeMember(targetTeamId, userId);
      if (result.success) {
        fetchTeamData();
      } else {
        alert('เกิดข้อผิดพลาด: ' + result.error);
      }
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    // Use teamId from URL first, then fallback to currentTeam.id
    const targetTeamId = teamId || currentTeam?.id;
    if (!targetTeamId) return;
    
    const member = team?.members?.find(m => m.uid === userId);
    if (!member) return;
    
    const currentRole = member.teamRole;
    const roleLabel = newRole === TEAM_ROLES.DEPUTY_LEADER ? 'รองหัวหน้าทีม' : 'สมาชิก';
    const currentRoleLabel = currentRole === TEAM_ROLES.DEPUTY_LEADER ? 'รองหัวหน้าทีม' : 'สมาชิก';
    
    if (currentRole === newRole) {
      return;
    }
    
    if (!window.confirm(`คุณต้องการเปลี่ยนสิทธิ์ของ "${member.name}" จาก "${currentRoleLabel}" เป็น "${roleLabel}" หรือไม่?`)) {
      return;
    }
    
    const result = await changeMemberRole(targetTeamId, userId, newRole);
    if (result.success) {
      alert(`✅ เปลี่ยนสิทธิ์เป็น "${roleLabel}" สำเร็จ`);
      fetchTeamData();
    } else {
      alert('เกิดข้อผิดพลาด: ' + result.error);
    }
  };

  const handleInviteUser = async () => {
    if (!selectedUser) {
      alert('กรุณาเลือกผู้ใช้ที่ต้องการเชิญ');
      return;
    }

    // Use teamId from URL first, then fallback to currentTeam.id
    const targetTeamId = teamId || currentTeam?.id;
    if (!targetTeamId) return;

    // Check if user is already a member
    const isMember = team.members?.some(m => m.uid === selectedUser.uid);
    if (isMember) {
      alert('ผู้ใช้คนนี้เป็นสมาชิกของทีมแล้ว');
      return;
    }

    setInviting(true);
    try {
      const result = await inviteMember(targetTeamId, selectedUser.uid, TEAM_ROLES.MEMBER);
      if (result.success) {
        alert('ส่งคำเชิญสำเร็จ!');
        setShowInviteModal(false);
        setSelectedUser(null);
        fetchTeamRequests();
      } else {
        alert('เกิดข้อผิดพลาด: ' + result.error);
      }
    } catch (error) {
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">ไม่พบทีม</p>
          <button
            onClick={() => navigate('/nurse/teams')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            กลับไปหน้าทีม
          </button>
        </div>
      </div>
    );
  }

  const canManage = team.userRole === TEAM_ROLES.LEADER || team.userRole === TEAM_ROLES.DEPUTY_LEADER;
  const isLeader = team.userRole === TEAM_ROLES.LEADER;
  const isMember = team.members?.some(m => m.uid === currentUser?.uid);
  const hasPendingInvitation = pendingInvitation && !isMember;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/teams')}
            className="text-blue-600 hover:text-blue-700 mb-4"
          >
            ← กลับไปหน้าทีม
          </button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{team.name}</h1>
              <p className="text-gray-600 mt-2">{team.description || 'ไม่มีคำอธิบาย'}</p>
            </div>
            {canManage && (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    console.log('Navigating to /nurse/teams/config');
                    navigate('/nurse/teams/config');
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                >
                  ⚙️ จัดการตำแหน่งเก็บอุปกรณ์
                </button>
                <button
                  onClick={() => {
                    console.log('Navigating to /nurse/teams/beds');
                    navigate('/nurse/teams/beds');
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition relative z-10"
                >
                  🛏️ จัดการเตียงผู้ป่วย
                </button>
              </div>
            )}
          </div>
          
          {/* Pending Invitation Banner */}
          {hasPendingInvitation && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex-1">
                  <p className="font-semibold text-blue-800">📨 คุณได้รับคำเชิญเข้าร่วมทีมนี้</p>
                  <p className="text-sm text-blue-600 mt-1">
                    {pendingInvitation.invitedByName || 'หัวหน้าทีม'} เชิญคุณเข้าร่วมทีม
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleAcceptInvitation}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  >
                    ตอบรับ
                  </button>
                  <button
                    onClick={handleRejectInvitation}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                  >
                    ไม่ตอบรับ
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'members'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            สมาชิก ({team.members?.filter((member, index, self) => 
              index === self.findIndex(m => m.uid === member.uid)
            ).length || 0})
          </button>
          {canManage && (
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'requests'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              คำขอ ({requests.length})
            </button>
          )}
        </div>

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            {canManage && (
              <div className="mb-4">
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  + เชิญสมาชิก
                </button>
              </div>
            )}
            <div className="space-y-4">
              {team.members?.filter((member, index, self) => 
                // Remove duplicates: keep only first occurrence of each uid
                index === self.findIndex(m => m.uid === member.uid)
              ).map((member) => (
                <div
                  key={member.uid}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                      {member.name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{member.name}</p>
                      <p className="text-sm text-gray-600">
                        {member.role} • {member.teamRole === TEAM_ROLES.LEADER ? 'หัวหน้าทีม' :
                                         member.teamRole === TEAM_ROLES.DEPUTY_LEADER ? 'รองหัวหน้า' : 'สมาชิก'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <GiftButton
                      toUserId={member.uid}
                      toUserName={member.name}
                      context={GIFT_CONTEXTS.TEAM_APPRECIATION}
                      variant="small"
                    />
                    {isLeader && member.uid !== currentUser?.uid && member.teamRole !== TEAM_ROLES.LEADER && (
                      <div className="flex items-center gap-2">
                        {/* Role Change Dropdown */}
                        <select
                          value={member.teamRole}
                          onChange={(e) => handleChangeRole(member.uid, e.target.value)}
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value={TEAM_ROLES.MEMBER}>สมาชิก</option>
                          <option value={TEAM_ROLES.DEPUTY_LEADER}>รองหัวหน้าทีม</option>
                        </select>
                        <button
                          onClick={() => handleRemoveMember(member.uid)}
                          className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          ลบออก
                        </button>
                      </div>
                    )}
                    {canManage && !isLeader && member.uid !== currentUser?.uid && member.teamRole !== TEAM_ROLES.LEADER && (
                      <button
                        onClick={() => handleRemoveMember(member.uid)}
                        className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      >
                        ลบออก
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && canManage && (
          <div className="bg-white rounded-lg shadow-md p-6">
            {requests.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">ไม่มีคำขอที่รอการอนุมัติ</p>
              </div>
            ) : (
              <div className="space-y-4">
                {requests.map(request => (
                  <div
                    key={request.id}
                    className="p-4 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="font-semibold text-gray-800">{request.userName}</p>
                        <p className="text-sm text-gray-600">{request.userRole}</p>
                        {request.message && (
                          <p className="text-sm text-gray-600 mt-2">{request.message}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApproveRequest(request.id)}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                      >
                        อนุมัติ
                      </button>
                      <button
                        onClick={() => handleRejectRequest(request.id)}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                      >
                        ปฏิเสธ
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Invite Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h2 className="text-xl font-bold text-gray-800 mb-4">เชิญสมาชิกเข้าทีม</h2>
              <div className="mb-4">
                <UserSelector
                  onUserSelect={setSelectedUser}
                  selectedUser={selectedUser}
                />
              </div>
              {selectedUser && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">ผู้ใช้ที่เลือก:</p>
                  <p className="font-semibold">{selectedUser.displayName || selectedUser.email}</p>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
                >
                  ยกเลิก
                </button>
                <button
                  onClick={handleInviteUser}
                  disabled={!selectedUser || inviting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {inviting ? 'กำลังส่ง...' : 'ส่งคำเชิญ'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamDetailPage;


